<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetContractGeneralData',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    contract: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const labelAlign = ref('right');
  const categories = ref(null);
  const statuses = ref(null);
  const contractTypes = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    categories: categories.value,
    statuses: statuses.value,
    contractTypes: contractTypes.value,
    ux: ux.value,
  }));

  const $$load_categories = async () => {
    return $fn.value.fetch('list_contract_category');
  };
  const $$load_statuses = async () => {
    return $fn.value.getEnumValuesFromSchema('ContractStatus');
  };
  const $$load_contractTypes = async () => {
    return $fn.value.getEnumValuesFromSchema('ContractTypes');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'labelAlign' },
      { name: 'categories' },
      { name: 'statuses' },
      { name: 'contractTypes' },
      { name: 'ux' },
    ],
    loaders: {
      categories: $$load_categories,
      statuses: $$load_statuses,
      contractTypes: $$load_contractTypes,
    },
    ux: {},
    dataRefs: { labelAlign, categories, statuses, contractTypes, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const filteredCategories = computed(() => {
    if (loaded.value) {
      return categories.value.filter(
        (p) => p.contract_type === props.contract.contract_type
      );
    }
    return null;
  });

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );

  const act_7315092382398562_reloadComponentData_ja4n7tux7 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_25a9y0fyq =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_ja4n7tux7,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_25a9y0fyq],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_25a9y0fyq,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_25a9y0fyq = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_25a9y0fyq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <legend class="text-muted">
      {{ pgettext('Contract', 'General') }}
    </legend>
    <ItpFormGroup
      name="category"
      :label="gettext('Category')"
      :label-cols-md="4"
      :content-cols-md="2"
      v-if="filteredCategories && filteredCategories.length"
    >
      <ItpFormSelect2
        name="category"
        v-model="contract.category"
        :value="contract.category"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.category =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        bind-object
        textField="name"
        :options="filteredCategories"
      >
      </ItpFormSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      name="revision_number"
      :label="pgettext('Contract', 'Revision number')"
      :label-cols-md="4"
      :content-cols-md="2"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormInput
        type="number"
        name="revision_number"
        v-model="contract.revision_number"
        :value="contract.revision_number"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.revision_number =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        required
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="contract_type"
      :label="gettext('Contract type')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormSelect2
        name="contract_type"
        v-model="contract.contract_type"
        :value="contract.contract_type"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.contract_type =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :options="contractTypes"
        valueField="value"
        textField="text"
        required
      >
      </ItpFormSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      name="status"
      :label="pgettext('Contract', 'Status')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormSelect2
        name="status"
        v-model="contract.status"
        :value="contract.status"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.status =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        valueField="value"
        textField="text"
        :options="statuses"
        required
      >
      </ItpFormSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      name="external_key"
      :label="pgettext('Contract', 'External key')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="external_key"
        v-model="contract.external_key"
        :value="contract.external_key"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (contract.external_key =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
  </ItpUsercontrol>
</template>
